import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
	Calendar,
	DollarSign,
	CheckCircle,
	XCircle,
	Clock,
	AlertTriangle,
	Star,
	ArrowRight,
	Package,
	Shield,
} from "lucide-react";
import { api } from "@/lib/api";
import type { Booking, Tool, User } from "@/lib/types";
import { BOOKING_STATUS_LABELS } from "@/lib/types";

type EnrichedBooking = Booking & { tool?: Tool; borrower?: User; lender?: User };

interface BookingsPageProps {
	currentUserId: string;
	onViewTool: (toolId: string) => void;
	onReportDamage: (bookingId: string) => void;
	onRateUser: (booking: EnrichedBooking) => void;
}

const STATUS_STYLES: Record<string, { color: string; icon: typeof Clock }> = {
	pending: { color: "bg-yellow-50 text-yellow-700 border-yellow-200", icon: Clock },
	confirmed: { color: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle },
	active: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Package },
	returned: { color: "bg-purple-50 text-purple-700 border-purple-200", icon: CheckCircle },
	completed: { color: "bg-gray-50 text-gray-700 border-gray-200", icon: CheckCircle },
	cancelled: { color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
	disputed: { color: "bg-orange-50 text-orange-700 border-orange-200", icon: AlertTriangle },
};

export function BookingsPage({
	currentUserId,
	onViewTool,
	onReportDamage,
	onRateUser,
}: BookingsPageProps) {
	const [bookings, setBookings] = useState<EnrichedBooking[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadBookings();
	}, []);

	async function loadBookings() {
		setLoading(true);
		try {
			const data = await api.getBookings();
			setBookings(data);
		} catch (e) {
			console.error("Failed to load bookings:", e);
		} finally {
			setLoading(false);
		}
	}

	async function handleStatusUpdate(bookingId: string, status: string) {
		try {
			await api.updateBookingStatus(bookingId, status);
			loadBookings();
		} catch (e) {
			console.error("Failed to update status:", e);
		}
	}

	const borrowing = bookings.filter((b) => b.borrowerId === currentUserId);
	const lending = bookings.filter((b) => b.lenderId === currentUserId);

	function BookingCard({ booking, role }: { booking: EnrichedBooking; role: "borrower" | "lender" }) {
		const statusStyle = STATUS_STYLES[booking.status] || STATUS_STYLES.pending;
		const StatusIcon = statusStyle.icon;
		const otherUser = role === "borrower" ? booking.lender : booking.borrower;

		return (
			<Card className="overflow-hidden">
				<CardContent className="p-4">
					<div className="flex items-start justify-between gap-3">
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<h3
									className="font-medium text-sm truncate cursor-pointer hover:text-primary transition-colors"
									onClick={() => booking.tool && onViewTool(booking.tool.id)}
								>
									{booking.tool?.name || "Unknown Tool"}
								</h3>
								<Badge
									variant="outline"
									className={`text-[10px] shrink-0 ${statusStyle.color}`}
								>
									<StatusIcon className="w-3 h-3 mr-1" />
									{BOOKING_STATUS_LABELS[booking.status]}
								</Badge>
							</div>

							<div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
								<span className="flex items-center gap-1">
									<Calendar className="w-3 h-3" />
									{booking.startDate} <ArrowRight className="w-3 h-3" /> {booking.endDate}
								</span>
							</div>

							<div className="flex items-center gap-3 mt-2">
								{otherUser && (
									<div className="flex items-center gap-1.5">
										<Avatar className="h-5 w-5">
											<AvatarImage src={otherUser.avatar} />
											<AvatarFallback className="text-[8px]">
												{otherUser.name[0]}
											</AvatarFallback>
										</Avatar>
										<span className="text-xs text-muted-foreground">
											{role === "borrower" ? "Lender:" : "Borrower:"}{" "}
											{otherUser.name}
										</span>
									</div>
								)}
							</div>
						</div>

						<div className="text-right shrink-0">
							<p className="font-semibold text-sm">${booking.totalCost}</p>
							<p className="text-[10px] text-muted-foreground flex items-center gap-0.5 justify-end">
								<Shield className="w-3 h-3" />
								${booking.deposit} deposit
							</p>
							{booking.depositReturned && (
								<p className="text-[10px] text-emerald-600 mt-0.5">
									Deposit returned
								</p>
							)}
						</div>
					</div>

					{/* Action buttons */}
					<div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
						{booking.status === "pending" && role === "lender" && (
							<>
								<Button
									size="sm"
									variant="default"
									className="h-7 text-xs"
									onClick={() => handleStatusUpdate(booking.id, "confirmed")}
								>
									Accept
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-7 text-xs"
									onClick={() => handleStatusUpdate(booking.id, "cancelled")}
								>
									Decline
								</Button>
							</>
						)}
						{booking.status === "confirmed" && role === "lender" && (
							<Button
								size="sm"
								variant="default"
								className="h-7 text-xs"
								onClick={() => handleStatusUpdate(booking.id, "active")}
							>
								Mark as Picked Up
							</Button>
						)}
						{booking.status === "active" && (
							<>
								<Button
									size="sm"
									variant="default"
									className="h-7 text-xs"
									onClick={() => handleStatusUpdate(booking.id, "returned")}
								>
									Mark as Returned
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-7 text-xs text-orange-600"
									onClick={() => onReportDamage(booking.id)}
								>
									<AlertTriangle className="w-3 h-3 mr-1" />
									Report Damage
								</Button>
							</>
						)}
						{booking.status === "returned" && role === "lender" && (
							<>
								<Button
									size="sm"
									variant="default"
									className="h-7 text-xs"
									onClick={() => handleStatusUpdate(booking.id, "completed")}
								>
									<CheckCircle className="w-3 h-3 mr-1" />
									Complete & Return Deposit
								</Button>
								<Button
									size="sm"
									variant="outline"
									className="h-7 text-xs text-orange-600"
									onClick={() => onReportDamage(booking.id)}
								>
									<AlertTriangle className="w-3 h-3 mr-1" />
									Report Damage
								</Button>
							</>
						)}
						{booking.status === "completed" && (
							<Button
								size="sm"
								variant="outline"
								className="h-7 text-xs"
								onClick={() => onRateUser(booking)}
							>
								<Star className="w-3 h-3 mr-1" />
								Leave Rating
							</Button>
						)}
						{booking.status === "pending" && role === "borrower" && (
							<Button
								size="sm"
								variant="outline"
								className="h-7 text-xs"
								onClick={() => handleStatusUpdate(booking.id, "cancelled")}
							>
								Cancel Request
							</Button>
						)}
					</div>
				</CardContent>
			</Card>
		);
	}

	if (loading) {
		return (
			<div className="space-y-4">
				{Array.from({ length: 4 }).map((_, i) => (
					<div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
				))}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<h1 className="text-xl font-bold">My Bookings</h1>

			<Tabs defaultValue="borrowing">
				<TabsList>
					<TabsTrigger value="borrowing" className="gap-1.5">
						Borrowing
						{borrowing.length > 0 && (
							<Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
								{borrowing.length}
							</Badge>
						)}
					</TabsTrigger>
					<TabsTrigger value="lending" className="gap-1.5">
						Lending
						{lending.length > 0 && (
							<Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
								{lending.length}
							</Badge>
						)}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="borrowing" className="space-y-3 mt-4">
					{borrowing.length === 0 ? (
						<div className="text-center py-12">
							<Package className="w-10 h-10 mx-auto text-muted-foreground/40" />
							<p className="text-muted-foreground mt-3">No borrowing activity</p>
							<p className="text-sm text-muted-foreground mt-1">
								Browse tools to start borrowing
							</p>
						</div>
					) : (
						borrowing.map((b) => (
							<BookingCard key={b.id} booking={b} role="borrower" />
						))
					)}
				</TabsContent>

				<TabsContent value="lending" className="space-y-3 mt-4">
					{lending.length === 0 ? (
						<div className="text-center py-12">
							<Package className="w-10 h-10 mx-auto text-muted-foreground/40" />
							<p className="text-muted-foreground mt-3">No lending activity</p>
							<p className="text-sm text-muted-foreground mt-1">
								List a tool to start earning
							</p>
						</div>
					) : (
						lending.map((b) => (
							<BookingCard key={b.id} booking={b} role="lender" />
						))
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
